/**
 * 우리 반 활동지·교실 놀이 PPT 이벤트의 생성 스트림 공통 처리.
 *
 * 모바일에서 기다리다 화면을 끄거나 다른 앱으로 넘어가면 브라우저는 스트림을
 * TypeError("Load failed", "network error") 로 끊는다. 이 원문을 그대로 보여주면
 * 교사는 무슨 일인지 알 수 없으므로 code 로 구분해 페이지가 안내 문구를 고르게 한다.
 */

import { getTrackingErrorCode } from '../../utils/trackingErrorCode';
import { readSseJson } from '../../utils/sseJsonStream';

export const STREAM_CLOSED_CODE = 'stream_closed';
export const NETWORK_ERROR_CODE = 'network';

const codedError = (message, code, extra = {}) => {
  const error = new Error(message);
  error.code = code;
  Object.assign(error, extra);
  return error;
};

export const parseErrorDetail = async (response) => {
  const data = await response.json().catch(() => ({}));
  const detail = data?.detail;
  const code = detail && typeof detail === 'object' ? detail.code : null;
  const message = (
    (detail && typeof detail === 'object' ? detail.message : detail)
    || data?.message
    || data?.error
    || '요청을 처리하지 못했어요.'
  );
  const retryAfter = Number(response.headers?.get?.('Retry-After') || 0);
  return codedError(message, code, {
    status: response.status,
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 0,
  });
};

/** 요청 자체가 나가지 못한 경우(오프라인·화면 전환) TypeError 를 network 코드로 바꾼다. */
export const fetchEventStream = async (url, options, { networkMessage }) => {
  try {
    return await fetch(url, options);
  } catch (error) {
    throw codedError(networkMessage, NETWORK_ERROR_CODE, { cause: error });
  }
};

export const readSseJsonStream = async (response, { onEvent, timeoutMs, messages }) => {
  if (!response.body) {
    throw codedError(messages.closed, STREAM_CLOSED_CODE);
  }
  let completePayload = null;
  const events = readSseJson(response.body, {
    idleTimeoutMs: timeoutMs,
    idleTimeoutError: () => codedError(messages.timeout, 'timeout'),
    mapReadError: (streamError) => codedError(messages.closed, STREAM_CLOSED_CODE, { cause: streamError }),
  });
  for await (const eventData of events) {
    onEvent(eventData);
    if (eventData.type === 'page_preview') {
      const holdMs = Number(window.__WHATQUIZ_E2E_SSE_HOLD_MS || 0);
      if (holdMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, holdMs));
      }
    }
    if (eventData.type === 'complete') completePayload = eventData;
    if (eventData.type === 'error') {
      throw codedError(
        eventData.message || messages.failed,
        eventData.error_code || eventData.code || 'generation_failed',
      );
    }
  }
  if (!completePayload) {
    throw codedError(messages.closed, STREAM_CLOSED_CODE);
  }
  return completePayload;
};

const waitUntilVisible = () => new Promise((resolve) => {
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
    resolve();
    return;
  }
  const handleChange = () => {
    if (document.visibilityState === 'hidden') return;
    document.removeEventListener('visibilitychange', handleChange);
    resolve();
  };
  document.addEventListener('visibilitychange', handleChange);
});

export const isResumableGenerationCode = (code, inProgressCode) => (
  code === STREAM_CLOSED_CODE
  || code === NETWORK_ERROR_CODE
  || Boolean(inProgressCode && code === inProgressCode)
);

const PENDING_SAMPLE_TTL_MS = 30 * 60 * 1000;

/**
 * 이미지 단계까지 갔다가 끊긴 요청을 새로고침·탭 재진입 뒤에도 이어받게 보관한다.
 * 서버는 같은 sample_request_id 로 와야 저장된 결과를 다시 주므로 요청 body 를 그대로 둔다.
 * 입력(반 이름·주제 등)이나 로그인 계정이 바뀌었으면 화면과 다른 결과가 오지 않게 버린다.
 */
export const loadPendingSampleRequest = (storageKey, inputSignature) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (!saved) return null;
    const expired = Date.now() - (saved.savedAt || 0) > PENDING_SAMPLE_TTL_MS;
    if (!saved.body || expired || saved.inputSignature !== inputSignature) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return { body: saved.body, eventId: saved.eventId };
  } catch (_) {
    return null;
  }
};

export const savePendingSampleRequest = (storageKey, { body, eventId, inputSignature }) => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({
      body,
      eventId,
      inputSignature,
      savedAt: Date.now(),
    }));
  } catch (_) {
    // 저장이 안 되면 새로고침 뒤 이어받기만 못 한다.
  }
};

export const clearPendingSampleRequest = (storageKey) => {
  try {
    sessionStorage.removeItem(storageKey);
  } catch (_) {
    // 무시
  }
};

const delay = (ms) => new Promise((resolve) => { window.setTimeout(resolve, ms); });

/**
 * 무료 샘플 생성 요청. 서버는 연결이 끊겨도 끝까지 만들어 결과를 저장하고,
 * 같은 sample_request_id 로 다시 오면 그 결과를 돌려준다(replayed).
 * 그래서 끊김·네트워크·'만드는 중'이면 화면이 다시 보일 때 같은 요청을 다시 보낸다.
 */
export const streamEventSample = async ({
  url,
  init,
  readOptions,
  networkMessage,
  inProgressCode,
  resumeDeadlineMs,
  retryDelayMs = 5000,
  onResume,
}) => {
  const deadline = Date.now() + resumeDeadlineMs;
  let resumeCount = 0;
  while (true) {
    try {
      const response = await fetchEventStream(url, init, { networkMessage });
      if (!response.ok) throw await parseErrorDetail(response);
      const complete = await readSseJsonStream(response, readOptions);
      return { complete, resumed: resumeCount > 0 };
    } catch (error) {
      if (!isResumableGenerationCode(error?.code, inProgressCode) || Date.now() >= deadline) {
        throw error;
      }
      resumeCount += 1;
      onResume?.({ code: error.code, attempt: resumeCount });
      await waitUntilVisible();
      await delay(retryDelayMs);
    }
  }
};

/**
 * 실패를 교사에게 보여줄 문구와 계측 action 으로 나눈다.
 * 무료 소진은 실패가 아니라 가입 전환 지점이라 quota_reached 로 따로 센다.
 */
export const describeEventGenerationError = (error, {
  alreadyUsedCode,
  inProgressCode,
  messages,
}) => {
  const code = typeof error?.code === 'string' ? error.code : '';
  if (code === alreadyUsedCode) {
    return { action: 'quota_reached', code, exhausted: true, message: messages.alreadyUsed };
  }
  if (code === inProgressCode) {
    return { action: 'generation_failed', code, exhausted: false, message: messages.inProgress };
  }
  // fetchWithRetry 는 재시도 뒤 브라우저 TypeError/AbortError 를 그대로 던진다.
  if (!code && (error instanceof TypeError || error?.name === 'AbortError')) {
    return {
      action: 'generation_failed',
      code: NETWORK_ERROR_CODE,
      exhausted: false,
      message: messages.network,
    };
  }
  return {
    action: 'generation_failed',
    code: getTrackingErrorCode(error),
    exhausted: false,
    message: error?.message || messages.failed,
  };
};
